import { useState, useRef, useCallback } from 'react';
import { getAccessToken, triggerReauth } from '../../../auth/AuthProvider';
import { createConcurrencyPool } from '../../../utils/concurrencyPool';
import { createPipelineFileEntry } from '../utils/pipelineFileUtils';
import { MAX_SOURCE_PDF_BYTES, oversizeMessage } from '../../../utils/uploadLimits';

const xmpApiBaseUrl = process.env.REACT_APP_XMP_EDITOR_URL
  || process.env.REACT_APP_API_URL
  || window.location.origin;

const coverApiBaseUrl = process.env.REACT_APP_ADD_COVER_URL
  || process.env.REACT_APP_API_URL
  || window.location.origin;

// Bounds how many files this client works on at once (each file's own two
// stages always run sequentially, never in parallel with each other) — same
// rationale and value as AddCoverService/hooks/useCoverBatchUpload.js.
const MAX_CONCURRENT_FILES = 3;

function coverStatusToMessage(status) {
  switch (status) {
    case 400: return 'No EAN-13 found in filename';
    case 404: return 'Product or cover image not found for this EAN';
    case 422: return 'Product data incomplete, or cover/content dimensions mismatch';
    case 413: return "File exceeds the server's size limit";
    default: return `HTTP ${status}`;
  }
}

async function readBlobTextSafely(blob) {
  try {
    const text = await blob.text();
    return text || null;
  } catch {
    return null;
  }
}

function readJsonHeader(xhr, name) {
  const raw = xhr.getResponseHeader(name);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function useXmpCoverPipeline() {
  const [files, setFiles] = useState([]);
  // Mirrors `files` but updated by direct synchronous assignment, not through
  // React's state scheduling — createConcurrencyPool's push() can synchronously
  // invoke a file's processFile() (for however many fit under the concurrency
  // limit) in the very same tick as addFiles() calls it, which is before a
  // useEffect-based mirror would ever have run. Reading a plain ref that's kept
  // synchronously current avoids that race entirely.
  const filesRef = useRef([]);

  const applyFilesUpdate = useCallback((updater) => {
    filesRef.current = updater(filesRef.current);
    setFiles(filesRef.current);
  }, []);

  const poolRef = useRef(createConcurrencyPool(MAX_CONCURRENT_FILES));

  const updateStage = useCallback((id, stageName, patch) => {
    applyFilesUpdate((prev) => prev.map((entry) => (
      entry.id === id
        ? { ...entry, stages: { ...entry.stages, [stageName]: { ...entry.stages[stageName], ...patch } } }
        : entry
    )));
  }, [applyFilesUpdate]);

  const setStageSelected = useCallback((id, stageName, selected) => {
    applyFilesUpdate((prev) => prev.map((entry) => (
      entry.id === id
        ? { ...entry, selectedStages: { ...entry.selectedStages, [stageName]: selected } }
        : entry
    )));
  }, [applyFilesUpdate]);

  // Resolves the freshest selectedStages for a file at the instant it's needed —
  // a file can sit queued in the concurrency pool for a while, and the checkbox
  // must stay togglable during that wait, so this must never read a value
  // captured back when the task was first pushed onto the pool.
  const currentSelectedStages = useCallback((id) => {
    const entry = filesRef.current.find((f) => f.id === id);
    return entry ? entry.selectedStages : { xmp: false, cover: false };
  }, []);

  // Raw XHR (not apiFetch) for upload progress, matching useCoverBatchUpload.js.
  // Returns the successful XmpEditResult, or null (having already recorded the
  // error into state) on any failure.
  const runXmpStage = useCallback((id, file) => {
    updateStage(id, 'xmp', { status: 'uploading', progressPct: 0, error: null, s3Url: null, changes: [] });

    return new Promise((resolve) => {
      getAccessToken().then((token) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${xmpApiBaseUrl}/api/xmp/edit`);
        xhr.responseType = 'text';
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            updateStage(id, 'xmp', { status: 'uploading', progressPct: Math.round((e.loaded / e.total) * 100) });
          }
        };
        xhr.upload.onload = () => updateStage(id, 'xmp', { status: 'processing', progressPct: 100 });

        xhr.onload = () => {
          if (xhr.status === 401) {
            triggerReauth();
            resolve(null);
            return;
          }
          if (xhr.status === 200) {
            let results;
            try {
              results = JSON.parse(xhr.responseText);
            } catch {
              updateStage(id, 'xmp', { status: 'error', error: 'Could not parse xmp-ingest response' });
              resolve(null);
              return;
            }
            const result = results[0];
            if (result && result.status === 'success') {
              updateStage(id, 'xmp', {
                status: 'done', error: null, s3Url: result.s3Url, changes: result.changes || [],
              });
              resolve(result);
            } else {
              updateStage(id, 'xmp', { status: 'error', error: (result && result.message) || 'XMP processing failed' });
              resolve(null);
            }
          } else {
            updateStage(id, 'xmp', { status: 'error', error: xhr.responseText || `HTTP ${xhr.status}` });
            resolve(null);
          }
        };
        xhr.onerror = () => {
          updateStage(id, 'xmp', { status: 'error', error: 'Network error' });
          resolve(null);
        };

        const formData = new FormData();
        formData.append('files', file);
        xhr.send(formData);
      });
    });
  }, [updateStage]);

  // sourceFromXmp is the successful XmpEditResult when chaining from a completed
  // xmp stage (sends sourceUrl+filename, no re-upload); null/undefined means send
  // the raw file (cover running standalone, xmp not selected).
  const runCoverStage = useCallback((id, file, filename, sourceFromXmp) => {
    updateStage(id, 'cover', {
      status: 'uploading', progressPct: 0, error: null, resultBlob: null, dimensionInfo: null, resampleInfo: null,
    });

    return new Promise((resolve) => {
      getAccessToken().then((token) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${coverApiBaseUrl}/api/cover/bind`);
        xhr.responseType = 'blob';
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            updateStage(id, 'cover', { status: 'uploading', progressPct: Math.round((e.loaded / e.total) * 100) });
          }
        };
        xhr.upload.onload = () => updateStage(id, 'cover', { status: 'processing', progressPct: 100 });

        xhr.onload = async () => {
          if (xhr.status === 401) {
            triggerReauth();
            resolve(false);
            return;
          }
          const dimensionInfo = readJsonHeader(xhr, 'X-Cover-Dimension-Info');
          const resampleInfo = readJsonHeader(xhr, 'X-Cover-Resample-Info');
          if (xhr.status === 200) {
            updateStage(id, 'cover', {
              status: 'done', error: null, resultBlob: xhr.response, dimensionInfo, resampleInfo,
            });
            resolve(true);
          } else {
            const serverMessage = await readBlobTextSafely(xhr.response);
            updateStage(id, 'cover', {
              status: 'error', error: serverMessage || coverStatusToMessage(xhr.status), dimensionInfo, resampleInfo,
            });
            resolve(false);
          }
        };
        xhr.onerror = () => {
          updateStage(id, 'cover', { status: 'error', error: 'Network error' });
          resolve(false);
        };

        const formData = new FormData();
        if (sourceFromXmp) {
          formData.append('sourceUrl', sourceFromXmp.s3Url);
          formData.append('filename', filename);
        } else {
          formData.append('file', file);
        }
        xhr.send(formData);
      });
    });
  }, [updateStage]);

  const processFile = useCallback(async (id, file, filename) => {
    let xmpResult = null;
    const stagesAtXmpStart = currentSelectedStages(id);

    if (stagesAtXmpStart.xmp) {
      if (file.size > MAX_SOURCE_PDF_BYTES) {
        updateStage(id, 'xmp', { status: 'error', error: oversizeMessage(file) });
        if (currentSelectedStages(id).cover) {
          updateStage(id, 'cover', { status: 'error', error: 'Not run — XMP stage failed.' });
        }
        return;
      }
      xmpResult = await runXmpStage(id, file);
      if (!xmpResult) {
        // Don't silently run cover against the un-edited original when xmp was
        // explicitly requested and failed — that would produce a file that looks
        // finished but is missing what was asked for.
        if (currentSelectedStages(id).cover) {
          updateStage(id, 'cover', { status: 'error', error: 'Not run — XMP stage failed.' });
        }
        return;
      }
    } else {
      updateStage(id, 'xmp', { status: 'skipped' });
    }

    const stagesAtCoverStart = currentSelectedStages(id);
    if (!stagesAtCoverStart.cover) {
      updateStage(id, 'cover', { status: 'skipped' });
      return;
    }
    if (!xmpResult && file.size > MAX_SOURCE_PDF_BYTES) {
      updateStage(id, 'cover', { status: 'error', error: oversizeMessage(file) });
      return;
    }
    await runCoverStage(id, file, filename, xmpResult);
  }, [currentSelectedStages, runXmpStage, runCoverStage, updateStage]);

  const addFiles = useCallback((newFiles) => {
    const entries = newFiles.map(createPipelineFileEntry);
    applyFilesUpdate((prev) => [...prev, ...entries]);
    entries.forEach((entry) => {
      poolRef.current.push(() => processFile(entry.id, entry.file, entry.filename));
    });
  }, [applyFilesUpdate, processFile]);

  // Always restarts from the original file using whatever selectedStages is set
  // to right now — no intermediate pipeline state (e.g. a prior xmp result) is
  // ever resumed from, matching the retry-semantics decision made earlier.
  const retry = useCallback((entry) => {
    const queuedOrSkipped = (selected) => (selected ? 'queued' : 'skipped');
    updateStage(entry.id, 'xmp', {
      status: queuedOrSkipped(entry.selectedStages.xmp), progressPct: 0, error: null, s3Url: null, changes: [],
    });
    updateStage(entry.id, 'cover', {
      status: queuedOrSkipped(entry.selectedStages.cover), progressPct: 0, error: null, resultBlob: null,
      dimensionInfo: null, resampleInfo: null,
    });
    poolRef.current.push(() => processFile(entry.id, entry.file, entry.filename));
  }, [processFile, updateStage]);

  return { files, addFiles, retry, setStageSelected };
}
